import { Controller } from "@hotwired/stimulus"

/**
 * Image Dropzone Controller
 *
 * A drag-and-drop file upload controller that works with standard Rails forms.
 * Supports single or multiple file uploads with image previews.
 *
 * Usage:
 *   <div data-controller="image-dropzone"
 *        data-image-dropzone-multiple-value="false"
 *        data-image-dropzone-max-file-size-value="10"
 *        data-image-dropzone-accepted-files-value="image/*">
 *     <div data-image-dropzone-target="dropArea">
 *       <!-- Drop zone UI -->
 *     </div>
 *     <div data-image-dropzone-target="preview" class="hidden">
 *       <!-- Previews will be inserted here -->
 *     </div>
 *     <input type="file" data-image-dropzone-target="input" class="hidden">
 *     <div data-image-dropzone-target="error" class="hidden"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["dropArea", "preview", "input", "error", "existingImages"]
  static values = {
    multiple: { type: Boolean, default: false },
    maxFileSize: { type: Number, default: 10 }, // MB
    acceptedFiles: { type: String, default: "image/*" },
  }

  connect() {
    this.files = []
    this.setupDropArea()
    this.setupInput()
  }

  setupDropArea() {
    if (!this.hasDropAreaTarget) return

    this.dropAreaTarget.addEventListener("click", (e) => {
      if (e.target.closest("[data-action]")) return // Don't trigger if clicking a button
      this.inputTarget.click()
    })

    this.dropAreaTarget.addEventListener("dragover", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.dropAreaTarget.classList.add("border-blue-400", "bg-blue-50")
    })

    this.dropAreaTarget.addEventListener("dragleave", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.dropAreaTarget.classList.remove("border-blue-400", "bg-blue-50")
    })

    this.dropAreaTarget.addEventListener("drop", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.dropAreaTarget.classList.remove("border-blue-400", "bg-blue-50")
      this.handleFiles(e.dataTransfer.files)
    })
  }

  setupInput() {
    if (!this.hasInputTarget) return

    this.inputTarget.addEventListener("change", (e) => {
      this.handleFiles(e.target.files)
    })
  }

  handleFiles(fileList) {
    const files = Array.from(fileList)
    this.hideError()

    for (const file of files) {
      // Validate file type
      if (!this.isValidFileType(file)) {
        this.showError(`"${file.name}" is not a valid image file.`)
        continue
      }

      // Validate file size
      if (!this.isValidFileSize(file)) {
        this.showError(`"${file.name}" exceeds the maximum file size of ${this.maxFileSizeValue}MB.`)
        continue
      }

      if (this.multipleValue) {
        this.files.push(file)
      } else {
        this.files = [file]
      }
    }

    this.updateFileInput()
    this.renderPreviews()
  }

  isValidFileType(file) {
    const accepted = this.acceptedFilesValue
    if (accepted === "image/*") {
      return file.type.startsWith("image/")
    }
    const types = accepted.split(",").map((t) => t.trim())
    return types.some((type) => {
      if (type.endsWith("/*")) {
        return file.type.startsWith(type.replace("/*", "/"))
      }
      return file.type === type || file.name.toLowerCase().endsWith(type)
    })
  }

  isValidFileSize(file) {
    return file.size <= this.maxFileSizeValue * 1024 * 1024
  }

  updateFileInput() {
    if (!this.hasInputTarget) return

    // Create a new DataTransfer to set files on the input
    const dataTransfer = new DataTransfer()
    this.files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  renderPreviews() {
    if (!this.hasPreviewTarget) return

    this.previewTarget.innerHTML = ""

    if (this.files.length === 0) {
      this.previewTarget.classList.add("hidden")
      if (this.hasDropAreaTarget) {
        this.dropAreaTarget.classList.remove("hidden")
      }
      return
    }

    this.previewTarget.classList.remove("hidden")

    // In single mode, hide existing/cached images when a new file is selected
    if (!this.multipleValue && this.hasExistingImagesTarget) {
      this.existingImagesTarget.classList.add("hidden")
    }

    if (this.multipleValue) {
      // Multiple files: show grid of previews
      const grid = document.createElement("div")
      grid.className = "flex flex-wrap gap-3"

      this.files.forEach((file, index) => {
        const preview = this.createPreviewElement(file, index)
        grid.appendChild(preview)
      })

      // Add "add more" button
      const addMore = document.createElement("div")
      addMore.className = "w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
      addMore.innerHTML = `
        <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
      `
      addMore.addEventListener("click", () => this.inputTarget.click())
      grid.appendChild(addMore)

      this.previewTarget.appendChild(grid)
    } else {
      // Single file: show larger preview with option to change
      const file = this.files[0]
      const container = document.createElement("div")
      container.className = "inline-block relative group"

      const img = document.createElement("img")
      img.className = "max-h-32 rounded-lg border border-gray-200"

      const reader = new FileReader()
      reader.onload = (e) => {
        img.src = e.target.result
      }
      reader.readAsDataURL(file)

      container.appendChild(img)

      // Remove button overlay
      const overlay = document.createElement("div")
      overlay.className = "absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center"
      overlay.innerHTML = `
        <button type="button" class="text-white text-sm font-medium" data-action="click->image-dropzone#removeFile" data-index="0">
          Remove
        </button>
      `
      container.appendChild(overlay)

      this.previewTarget.appendChild(container)

      // File info
      const info = document.createElement("p")
      info.className = "mt-2 text-sm text-gray-500"
      info.textContent = `${file.name} (${this.formatFileSize(file.size)})`
      this.previewTarget.appendChild(info)

      // Hide drop area when preview is shown for single file
      if (this.hasDropAreaTarget) {
        this.dropAreaTarget.classList.add("hidden")
      }
    }
  }

  createPreviewElement(file, index) {
    const container = document.createElement("div")
    container.className = "relative group"

    const img = document.createElement("img")
    img.className = "w-20 h-20 object-cover rounded-lg border border-gray-200"

    const reader = new FileReader()
    reader.onload = (e) => {
      img.src = e.target.result
    }
    reader.readAsDataURL(file)

    container.appendChild(img)

    // Remove button overlay
    const overlay = document.createElement("div")
    overlay.className = "absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center"
    overlay.innerHTML = `
      <button type="button" class="text-white" data-action="click->image-dropzone#removeFile" data-index="${index}">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `
    container.appendChild(overlay)

    return container
  }

  removeFile(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.files.splice(index, 1)
    this.updateFileInput()
    this.renderPreviews()
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
      this.errorTarget.textContent = ""
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }
}
